import { __ } from "@wordpress/i18n";
import React, { useState, useEffect } from 'react';
import apiFetch from '@wordpress/api-fetch';
import withForm from '../../layouts/withForm';
import './SiteHealthInfo.scss';


const SiteHealthInfo = () => {
  const [siteInfo, setSiteInfo] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [tabContent, setTabContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [printed, setPrinted] = useState(false);

  // Fetch site health info and tabs on component mount
  useEffect(() => {
      fetchSiteHealthInfo();
  }, []);

  // Add CSS styles for smooth transitions
  const styles = {
    fadeTransition: {
      transition: 'opacity 0.3s ease-in-out',
      opacity: loading ? 0 : 1,
    },
    tabsContainer: {
      minHeight: '50px', // Set a minimum height for tabs section
    },
    panelContainer: {
      minHeight: '300px', // Set a minimum height for panel
      position: 'relative',
      transition: 'height 0.3s ease-in-out',
    },
    panelBody: {
      transition: 'opacity 0.3s ease-in-out',
      opacity: loading ? 0 : 1,
    },
    printFrame: {
      position: 'absolute',
      height: '0px',
      width: '0px',
      border: '0px',
      left: '-1000px',
      top: '-1000px',
    }
  };

  // Fetch tab content when active tab changes
  useEffect(() => {
      if (activeTab) {
          setLoading(true);
          fetchTabContent(activeTab);
      }
  }, [activeTab]);

  // Fetch site health info and tabs
  const fetchSiteHealthInfo = async () => {
      try {
          setLoading(true);
          const response = await apiFetch({ 
              path: '/ultimate-security/v1/site-health-info'
          });
          
          setSiteInfo(response.data);
          setTabs(response.tabs);
          
          // Set default active tab to the first tab if available
          if (response.tabs && response.tabs.length > 0) {
              setActiveTab(response.tabs[0].id);
          }
          
          setLoading(false);
      } catch (error) {
          console.error('Error fetching site health info:', error);
          setLoading(false);
      }
  };

  // Fetch content for a specific tab
  const fetchTabContent = async (tabId) => {
      try {
          setLoading(true);
          
          // Special handling for Must Use Plugins tab
          if (tabId === 'mu-plugins') {
              const response = await apiFetch({
                  path: '/ultimate-security/v1/site-health/mu-plugins'
              });
              
              // Set the tab content with proper formatting
              setTabContent({
                  id: 'mu-plugins',
                  label: __('Must Use Plugins', 'ultimate-security'),
                  plugins: response.plugins || []
              });
          }
          else {
              const response = await apiFetch({
                  path: `/ultimate-security/v1/site-health/${tabId}`
              });
            //   console.log(response);
              setTabContent(response);
          }
          
          setLoading(false);
      } catch (error) {
          console.error(`Error fetching tab content for ${tabId}:`, error);
          setLoading(false);
      }
  };

  // Handle tab click
  const handleTabClick = (tabId) => {
      setActiveTab(tabId);
  };

  // Handle copy to clipboard
  const handleCopyContent = () => {
      if (!tabContent) return;
      
      // Prepare content to be copied
      let copyText = "";
      
      // Handle different tab content structures
      if (activeTab === 'mu-plugins' && tabContent.plugins) {
          copyText = tabContent.plugins.map(plugin => 
              `${plugin.name} (Version ${plugin.version})`
          ).join('\n');
      } else if (tabContent.data) {
          copyText = tabContent.data.map(item => 
              `${item.label}: ${item.value}`
          ).join('\n');
      }
      
      // Copy to clipboard using the fallback method
      copyTextToClipboard(copyText).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
      }).catch(err => {
          console.error('Failed to copy text: ', err);
      });
  };

  // Handle copy all site info to clipboard
  const handleCopyAllSiteInfo = async () => {
      try {
          // Collect all available tab data into a single text
          let allInfoText = "### WORDPRESS SITE HEALTH INFO ###\n\n";
          
          // First, get the currently active tab data
          if (tabContent) {
              allInfoText += `## ${tabContent.label || "Current Tab"} ##\n`;
              
              if (activeTab === 'mu-plugins' && tabContent.plugins) {
                  tabContent.plugins.forEach(plugin => {
                      allInfoText += `${plugin.name}: Version ${plugin.version}\n`;
                      if (plugin.description) {
                          allInfoText += `Description: ${plugin.description}\n`;
                      }
                      allInfoText += '\n';
                  });
              } else if (tabContent.data) {
                  tabContent.data.forEach(item => {
                      allInfoText += `${item.label}: ${item.value}\n`;
                      if (item.description) {
                          allInfoText += `Description: ${item.description}\n`;
                      }
                  });
              }
              
              allInfoText += '\n';
          }
          
          // Then try to fetch data for all available tabs
          if (tabs && tabs.length > 0) {
              // Store current active tab to restore it later
              const currentTab = activeTab;
              
              // Create promises for all tab data fetches
              const tabPromises = tabs.map(async (tab) => {
                  if (tab.id === activeTab) return null; // Skip current tab as we already have its data
                  
                  try {
                      let response;
                      if (tab.id === 'mu-plugins') {
                          response = await apiFetch({
                              path: '/ultimate-security/v1/site-health/mu-plugins'
                          });
                          
                          return {
                              id: tab.id,
                              label: tab.label,
                              isPlugin: true,
                              data: response.plugins || []
                          };
                      } else {
                          response = await apiFetch({
                              path: `/ultimate-security/v1/site-health/${tab.id}`
                          });
                          return {
                              id: tab.id,
                              label: tab.label,
                              isPlugin: false,
                              data: response.data || []
                          };
                      }
                  } catch (error) {
                    //   console.error(`Error fetching data for tab ${tab.id}:`, error);
                      return {
                          id: tab.id,
                          label: tab.label,
                          error: true
                      };
                  }
              });
              
              // Wait for all tab data to be fetched
              const tabData = await Promise.all(tabPromises);
              
              // Add each tab's data to the text
              tabData.forEach(tab => {
                  if (!tab) return; // Skip null tabs (current active tab)
                  
                  allInfoText += `## ${tab.label} ##\n`;
                  
                  if (tab.error) {
                      allInfoText += `[Error fetching data for this section]\n\n`;
                      return;
                  }
                  
                  if (tab.isPlugin) {
                      tab.data.forEach(plugin => {
                          allInfoText += `${plugin.name}: Version ${plugin.version}\n`;
                          if (plugin.description) {
                              allInfoText += `Description: ${plugin.description}\n`;
                          }
                          allInfoText += '\n';
                      });
                  } else {
                      tab.data.forEach(item => {
                          allInfoText += `${item.label}: ${item.value}\n`;
                          if (item.description) {
                              allInfoText += `Description: ${item.description}\n`;
                          }
                      });
                      allInfoText += '\n';
                  }
              });
          } else if (siteInfo) {
              // If we have siteInfo data but no tabs, use that
              Object.keys(siteInfo).forEach(section => {
                  allInfoText += `## ${section.toUpperCase()} ##\n`;
                  
                  if (Array.isArray(siteInfo[section])) {
                      siteInfo[section].forEach(item => {
                          allInfoText += `${item.label}: ${item.value}\n`;
                          if (item.description) {
                              allInfoText += `Description: ${item.description}\n`;
                          }
                      });
                  } else {
                      allInfoText += `[Data format not recognized for this section]\n`;
                  }
                  
                  allInfoText += '\n';
              });
          }
          
          // Copy to clipboard using a fallback method if navigator.clipboard is not available
          await copyTextToClipboard(allInfoText);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        //   console.error('Error copying site info:', error);
          // Show some error feedback to the user
          alert(__('Failed to copy site information to clipboard. See console for details.', 'ultimate-security'));
      }
  };
  
  // Helper function to copy text to clipboard with fallback
  const copyTextToClipboard = (text) => {
      return new Promise((resolve, reject) => {
          // Try using the Clipboard API first
          if (navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(text)
                  .then(resolve)
                  .catch((clipboardError) => {
                      console.warn('Clipboard API failed:', clipboardError);
                      // Fallback to textarea method
                      fallbackCopyTextToClipboard(text, resolve, reject);
                  });
          } else {
              // Fallback for browsers without clipboard API support
              fallbackCopyTextToClipboard(text, resolve, reject);
          }
      });
  };
  
  // Fallback clipboard copy method using textarea
  const fallbackCopyTextToClipboard = (text, resolve, reject) => {
      try {
          // Create textarea element
          const textArea = document.createElement('textarea');
          textArea.value = text;
          
          // Make the textarea out of viewport
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          
          // Select and copy
          textArea.focus();
          textArea.select();
          
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (successful) {
              resolve();
          } else {
              reject(new Error('execCommand copy failed'));
          }
      } catch (err) {
          console.error('Fallback clipboard method failed:', err);
          reject(err);
      }
  };

  // Handle print for current tab content only
  const handlePrint = () => {
      if (!tabContent) return;
      
      try {
          // Get tab label for the header
          const tabLabel = tabContent.label || 
              (activeTab && tabs.find(tab => tab.id === activeTab)?.label) || 
              "WordPress Site Health Info";
          
          // Create print content based on the tab type
          let printContent = `<html>
              <head>
                  <title>${tabLabel} - WordPress Site Health</title>
                  <style>
                      body { font-family: Arial, sans-serif; padding: 20px; }
                      h2 { color: #23282d; }
                      .item { margin-bottom: 15px; }
                      .label { font-weight: bold; }
                      .description { font-style: italic; color: #646970; margin-top: 5px; }
                  </style>
              </head>
              <body>
                  <h2>${tabLabel}</h2>`;
          
          // Add tab content to print content
          if (activeTab === 'mu-plugins' && tabContent.plugins) {
              if (tabContent.plugins.length > 0) {
                  tabContent.plugins.forEach(plugin => {
                      printContent += `
                          <div class="item">
                              <div class="label">${plugin.name}</div>
                              <div class="value">Version ${plugin.version}</div>
                              ${plugin.description ? `<div class="description">${plugin.description}</div>` : ''}
                          </div>`;
                  });
              } else {
                  printContent += `<div>No Must Use Plugins found.</div>`;
              }
          } else if (tabContent.data) {
              tabContent.data.forEach(item => {
                  printContent += `
                      <div class="item">
                          <div class="label">${item.label}</div>
                          <div class="value">${item.value}</div>
                          ${item.description ? `<div class="description">${item.description}</div>` : ''}
                      </div>`;
              });
          } else {
              printContent += `<div>No information available for this tab.</div>`;
          }
          
          printContent += `
                  <div style="margin-top: 30px; font-size: 12px; color: #646970;">
                      Printed from WordPress Site Health Info on ${new Date().toLocaleString()}
                  </div>
              </body>
          </html>`;
          
          // Create a hidden iframe for printing
          const printFrame = document.createElement('iframe');
          printFrame.style.position = 'absolute';
          printFrame.style.height = '0px';
          printFrame.style.width = '0px';
          printFrame.style.border = '0px';
          printFrame.style.left = '-1000px';
          printFrame.style.top = '-1000px';
          
          document.body.appendChild(printFrame);
          
          // Write content to the iframe and print it
          printFrame.contentDocument.open();
          printFrame.contentDocument.write(printContent);
          printFrame.contentDocument.close();
          
          // Wait for content to load before printing
          printFrame.onload = () => {
              try {
                  printFrame.contentWindow.focus();
                  printFrame.contentWindow.print();
                  
                  // Show "Printed" message temporarily
                  setPrinted(true);
                  setTimeout(() => setPrinted(false), 2000);
                  
                  // Clean up iframe after printing (with delay to ensure printing completes)
                  setTimeout(() => {
                      document.body.removeChild(printFrame);
                  }, 500);
              } catch (printError) {
                  console.error('Error during print:', printError);
                  document.body.removeChild(printFrame);
              }
          };
      } catch (error) {
        //   console.error('Error setting up print:', error);
          alert(__('Failed to print site information. See console for details.', 'ultimate-security'));
      }
  };

  // Render tab content based on tab type
  const renderTabContent = () => {
      if (!tabContent) {
          return <div>{__("Select a tab to view information", "ultimate-security")}</div>;
      }
      
      // Special rendering for Must Use Plugins tab
      if (activeTab === 'mu-plugins' && tabContent.plugins) {
          return (
              <div id="copy-content">
                  {tabContent.plugins.map((plugin, index) => (
                      <div className="site-health-plugin-item mb-4" key={index}>
                          <div className="d-flex flex-row w-100">
                              <div className="w-50 text-start">
                                  {plugin.name}
                              </div>
                              <div className="w-50 text-start">
                                  {__("Version", "ultimate-security")} {plugin.version}
                              </div>
                          </div>
                          {plugin.description && (
                              <div className="plugin-description mt-2">
                                  {plugin.description}
                              </div>
                          )}
                      </div>
                  ))}
                  {tabContent.plugins.length === 0 && (
                      <div className="no-plugins-message">
                          {__("No Must Use Plugins found.", "ultimate-security")}
                      </div>
                  )}
              </div>
          );
      }
      
      // Default rendering for other tabs
      if (tabContent.data) {
          return (
              <div id="copy-content">
                  {tabContent.data.map((item, index) => (
                      <div className="site-health-info-item mb-4" key={index}>
                          <div className="d-flex flex-row w-100">
                              <div className="w-50 text-start fw-bold">
                                  {item.label}
                              </div>
                              <div className="w-50 text-start">
                                  {item.value}
                              </div>
                          </div>
                          {item.description && (
                              <div className="item-description mt-2">
                                  {item.description}
                              </div>
                          )}
                      </div>
                  ))}
              </div>
          );
      }
      
      return <div>{__("No information available for this tab", "ultimate-security")}</div>;
  };

  // Group tabs into two rows for display
  const getGroupedTabs = () => {
      if (!tabs || tabs.length === 0) return { firstRow: [], secondRow: [] };
      
      const midpoint = Math.ceil(tabs.length / 2);
      const firstRow = tabs.slice(0, midpoint);
      const secondRow = tabs.slice(midpoint);
      
      return { firstRow, secondRow };
  };

  const { firstRow, secondRow } = getGroupedTabs();

    return (
        <>
            <div className="settings-container">
                <div className="form-container bg-white border">
                    <div className="form-header p-lg border-bottom">
                        <div className="page-heading-wrap">
                            <h4 className="section-title pl-heading fw-700 text-text">
                                {__("Site Health Info", "ultimate-security")}
                            </h4>
                            <div className="section-intro pl-body text-text">
                                <p>
                                  {__("This page can show you every detail about the configuration of your WordPress website. For any improvements that could be made, see the", "ultimate-security")}
                                  <a href={`${ultimate_security_ajax_obj.admin_url}site-health.php`} target="_blank" style={{padding:"0 5px"}}> 
                                    {__("Site Health Status", "ultimate-security")}
                                  </a> {__("page.", "ultimate-security")}
                                </p>
                                <p>{__("If you want to export a handy list of all the information on this page, you can use the button below to copy it to the clipboard. You can then paste it in a text file and save it to your device, or paste it in an email exchange with a support engineer or theme/plugin developer for example.", "ultimate-security")}</p>
                            </div>
                            <div className="mt-4">
                                <button 
                                    type="button" 
                                    className="button button-secondary" 
                                    onClick={handleCopyAllSiteInfo}
                                >
                                    {copied 
                                        ? __("Copied to clipboard!", "ultimate-security") 
                                        : __("Copy site info to clipboard", "ultimate-security")
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="form-body p-lg border-bottom">
                        <div className="setting-unit pb-lg border-bottom">
                            <h4 className="pl-heading-1 fw-600 text-text">
                                {__("WordPress Configuration Information", "ultimate-security")}
                            </h4>
                            <div className="section-intro pl-body text-text">
                                <p>{__("This information provides an overview of your WordPress setup and can help identify potential issues.", "ultimate-security")}</p>
                            </div>
                        </div>
                        <div className="setting-unit pb-lg border-bottom d-flex align-items-center justify-content-center">
                          <div className="debug-tabs mt-4" style={styles.tabsContainer}>
                              {loading && !tabContent ? (
                                  <div className="loading">{__("Loading tabs...", "ultimate-security")}</div>
                              ) : (
                                  <ul className="debug-tabs-list">
                                      <div className="first-row">
                                          {firstRow.map((tab) => (
                                              <li key={tab.id}>
                                                  <a
                                                      href="#" 
                                                      className={activeTab === tab.id ? 'active' : ''}
                                                      onClick={(e) => {
                                                          e.preventDefault();
                                                          handleTabClick(tab.id);
                                                      }}
                                                  >
                                                      {tab.label}
                                                      {tab.count !== undefined && ` (${tab.count})`}
                                                  </a>
                                              </li>
                                          ))}
                                      </div>
                                      <div className="second-row">
                                          {secondRow.map((tab) => (
                                              <li key={tab.id}>
                                                  <a
                                                      href="#" 
                                                      className={activeTab === tab.id ? 'active' : ''}
                                                      onClick={(e) => {
                                                          e.preventDefault();
                                                          handleTabClick(tab.id);
                                                      }}
                                                  >
                                                      {tab.label}
                                                      {tab.count !== undefined && ` (${tab.count})`}
                                                  </a>
                                              </li>
                                          ))}
                                      </div>
                                  </ul>
                              )}
                          </div>
                        </div>
                        <div className="setting-unit">
                          <div className="panel-wrapper" style={styles.panelContainer}>
                              <div className="panel">
                                  <div className="panel-header d-flex justify-content-between align-items-center">
                                      <span>
                                          {loading && !tabContent
                                              ? __("Loading...", "ultimate-security") 
                                              : tabContent && tabContent.label 
                                                  ? tabContent.label 
                                                  : activeTab && tabs.find(tab => tab.id === activeTab)?.label
                                          }
                                      </span>
                                  </div>
                                  <div className="panel-body" style={styles.panelBody}>
                                      {renderTabContent()}
                                  </div>
                                  <div className="panel-footer">
                                      {__(`Scroll down to see more.`, "ultimate-security")}
                                  </div>
                              </div>
                              <div className="d-flex align-items-center justify-content-between mt-lg">
                                  <div
                                      className="info"
                                      style={{
                                          background: "#D1C1FF",
                                          color: "#5007AA",
                                          border: "1px solid #5007AA",
                                          padding: "8px 12px",
                                          borderRadius: "4px",
                                      }}
                                  >
                                      ℹ️{" "}
                                      {__("If you need help, copy and paste the above information for faster support.", "ultimate-security")}
                                  </div>
                                  <div className="d-flex align-items-center action-buttons">
                                    <img src={ultimate_security_ajax_obj.imageUrl+'copy.svg' } alt="copy-icon"/>
                                      <button
                                          className="copy-content"
                                          onClick={handleCopyContent}
                                          style={{
                                              marginRight: "10px",
                                              padding: "5px 15px",
                                          }}
                                      >
                                          {copied 
                                              ? __("Copied!", "ultimate-security") 
                                              : __("Copy", "ultimate-security")
                                          }
                                      </button>
                                      <img src={ultimate_security_ajax_obj.imageUrl+'print.svg' } alt="print-icon"/>
                                      <button
                                          className="print-content"
                                          onClick={handlePrint}
                                          style={{ padding: "5px 15px" }}
                                      >
                                          {printed
                                              ? __("Printed!", "ultimate-security")
                                              : __("Print", "ultimate-security")
                                          }
                                      </button>
                                  </div>
                              </div>
                          </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </>
    );
};

export default withForm(SiteHealthInfo);